/**
 * Client-side encryption utilities for the relay.
 * Uses SubtleCrypto API for AES-256-GCM when available (secure context).
 * Falls back to plaintext pass-through in non-secure contexts (dev over HTTP).
 */

const encoder = new TextEncoder();
const decoder = new TextDecoder();

/** Whether real encryption is available (requires HTTPS or localhost). */
export const hasSubtleCrypto = typeof crypto !== 'undefined' && !!crypto.subtle;

if (!hasSubtleCrypto) {
  console.warn(
    '[relay] crypto.subtle unavailable — running without E2E encryption. ' +
    'Use HTTPS or localhost for real encryption.',
  );
}

/**
 * Generate a random AES-256 key using SubtleCrypto.
 */
export async function generateKey(): Promise<CryptoKey> {
  if (!hasSubtleCrypto) return null as unknown as CryptoKey;
  return crypto.subtle.generateKey(
    { name: 'AES-GCM', length: 256 },
    true,
    ['encrypt', 'decrypt'],
  );
}

/**
 * Import a raw key buffer as a CryptoKey.
 */
export async function importKey(rawKey: Uint8Array): Promise<CryptoKey> {
  if (!hasSubtleCrypto) return null as unknown as CryptoKey;
  return crypto.subtle.importKey(
    'raw',
    rawKey.buffer as ArrayBuffer,
    { name: 'AES-GCM', length: 256 },
    true,
    ['encrypt', 'decrypt'],
  );
}

/**
 * Export a CryptoKey to raw bytes.
 */
export async function exportKey(key: CryptoKey): Promise<Uint8Array> {
  if (!hasSubtleCrypto) return new Uint8Array(32);
  const buffer = await crypto.subtle.exportKey('raw', key);
  return new Uint8Array(buffer);
}

/**
 * Encrypt a string with AES-256-GCM.
 * Returns: [nonce:12][ciphertext]
 * In non-secure context: returns UTF-8 bytes (plaintext pass-through).
 */
export async function encrypt(
  plaintext: string,
  key: CryptoKey,
): Promise<Uint8Array> {
  if (!hasSubtleCrypto) return encoder.encode(plaintext);
  const nonce = crypto.getRandomValues(new Uint8Array(12));
  const encrypted = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv: nonce },
    key,
    encoder.encode(plaintext),
  );
  const result = new Uint8Array(12 + encrypted.byteLength);
  result.set(nonce, 0);
  result.set(new Uint8Array(encrypted), 12);
  return result;
}

/**
 * Decrypt AES-256-GCM ciphertext.
 * Input format: [nonce:12][ciphertext]
 * In non-secure context: decodes UTF-8 bytes (plaintext pass-through).
 */
export async function decrypt(
  ciphertext: Uint8Array,
  key: CryptoKey,
): Promise<string> {
  if (!hasSubtleCrypto) return decoder.decode(ciphertext);
  const nonce = ciphertext.slice(0, 12);
  const data = ciphertext.slice(12);
  const decrypted = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: nonce },
    key,
    data,
  );
  return decoder.decode(decrypted);
}

/**
 * Convert Uint8Array to base64 string.
 */
export function toBase64(data: Uint8Array): string {
  return btoa(String.fromCharCode(...data));
}

/**
 * Convert base64 string to Uint8Array.
 */
export function fromBase64(base64: string): Uint8Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}
